/**
 * Analytics Sagas
 * Handle async analytics operations
 */

import { takeEvery, select, call, all, type PayloadAction } from '@ufc/core'
import { analyticsActions } from './analytics.slice'
import { consentActions } from './consent.slice'
import {
  selectAnalyticsConsent,
  selectSessionId,
  selectPageViewStartTime,
} from './analytics.selectors'
import type {
  PageViewPayload,
  TrackEventPayload,
  IdentifyPayload,
  ErrorPayload,
  PerformancePayload,
  PageViewEvent,
  TrackEvent,
  IdentifyEvent,
  ErrorEvent,
  PerformanceEvent,
} from '../domain/types'
import {
  sanitizePii,
  getDeviceType,
  isRtl,
  getLocale,
  generateErrorFingerprint,
} from '../domain/utils'
import { analytics } from '../infrastructure/service/analytics.service'

// ============================================
// Helper Functions
// ============================================

/**
 * Build base event properties
 */
function buildBaseProperties(sessionId: string) {
  return {
    timestamp: new Date().toISOString(),
    sessionId,
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    deviceType: getDeviceType(),
    locale: getLocale(),
    isRtl: isRtl(),
  }
}

// ============================================
// Page View Saga
// ============================================

function* handlePageView(action: PayloadAction<PageViewPayload>) {
  const consent: boolean = yield select(selectAnalyticsConsent)
  if (!consent) return

  const sessionId: string = yield select(selectSessionId)
  const previousStartTime: number = yield select(selectPageViewStartTime)
  const previousPageDuration = Date.now() - previousStartTime

  const event: PageViewEvent = {
    ...buildBaseProperties(sessionId),
    type: 'page_view',
    title: action.payload.title || document.title,
    pagePath: action.payload.pagePath,
    routeName: action.payload.routeName,
    routeParams: action.payload.routeParams
      ? sanitizePii(action.payload.routeParams)
      : undefined,
    referrer: action.payload.referrer || document.referrer || undefined,
    previousPageDuration,
  }

  // Send to analytics service
  yield call([analytics, analytics.trackPageView], event)
}

// ============================================
// Track Event Saga
// ============================================

function* handleTrackEvent(action: PayloadAction<TrackEventPayload>) {
  const consent: boolean = yield select(selectAnalyticsConsent)
  if (!consent) return

  const sessionId: string = yield select(selectSessionId)

  const event: TrackEvent = {
    ...buildBaseProperties(sessionId),
    type: 'track',
    eventName: action.payload.eventName,
    category: action.payload.category,
    action: action.payload.action,
    label: action.payload.label,
    value: action.payload.value,
    properties: action.payload.properties
      ? sanitizePii(action.payload.properties)
      : undefined,
  }

  yield call([analytics, analytics.trackEvent], event)
}

// ============================================
// Identify Saga
// ============================================

function* handleIdentify(action: PayloadAction<IdentifyPayload>) {
  const consent: boolean = yield select(selectAnalyticsConsent)
  if (!consent) return

  const sessionId: string = yield select(selectSessionId)

  const event: IdentifyEvent = {
    ...buildBaseProperties(sessionId),
    type: 'identify',
    userId: action.payload.userId,
    traits: action.payload.traits
      ? sanitizePii(action.payload.traits)
      : {},
  }

  yield call([analytics, analytics.identify as any], event)
}

// ============================================
// Error Tracking Saga
// ============================================

function* handleTrackError(action: PayloadAction<ErrorPayload>) {
  // Errors are always tracked, regardless of consent
  const sessionId: string = yield select(selectSessionId)

  const error = typeof action.payload.error === 'string'
    ? new Error(action.payload.error)
    : action.payload.error

  const event: ErrorEvent = {
    ...buildBaseProperties(sessionId),
    type: 'error',
    errorName: error.name,
    errorMessage: error.message,
    stackTrace: error.stack?.substring(0, 2000),
    severity: action.payload.severity || 'medium',
    handled: action.payload.handled ?? true,
    componentName: action.payload.componentName,
    fingerprint: generateErrorFingerprint(error),
    context: action.payload.context
      ? sanitizePii(action.payload.context)
      : undefined,
  }

  yield call([analytics, analytics.trackError as any], event)
}

// ============================================
// Performance Saga
// ============================================

function* handleTrackPerformance(action: PayloadAction<PerformancePayload>) {
  const consent: boolean = yield select(selectAnalyticsConsent)
  if (!consent) return

  const sessionId: string = yield select(selectSessionId)

  const event: PerformanceEvent = {
    ...buildBaseProperties(sessionId),
    type: 'performance',
    metricName: action.payload.metricName,
    value: action.payload.value,
    rating: action.payload.rating,
    navigationType: action.payload.navigationType,
    attribution: action.payload.attribution,
  }

  yield call([analytics, analytics.trackPerformance], event)
}

// ============================================
// Consent Sync Saga
// ============================================

function* handleConsentAcceptAll() {
  yield call([analytics, analytics.setConsent], true)
}

function* handleConsentReject() {
  yield call([analytics, analytics.setConsent], false)
}

function* handleConsentUpdate(
  action: PayloadAction<{ category: string; granted: boolean; policyVersion: string }>
) {
  if (action.payload.category === 'analytics') {
    yield call([analytics, analytics.setConsent], action.payload.granted)
  }
}

// ============================================
// Reset Saga
// ============================================

function* handleReset() {
  yield call([analytics, analytics.reset])
}

// ============================================
// Root Saga
// ============================================

export function* analyticsSaga() {
  yield all([
    // Analytics actions
    takeEvery(analyticsActions.trackPageView.type, handlePageView),
    takeEvery(analyticsActions.trackEvent.type, handleTrackEvent),
    takeEvery(analyticsActions.identify.type, handleIdentify),
    takeEvery(analyticsActions.trackError.type, handleTrackError),
    takeEvery(analyticsActions.trackPerformance.type, handleTrackPerformance),
    takeEvery(analyticsActions.reset.type, handleReset),

    // Consent sync
    takeEvery(consentActions.acceptAll.type, handleConsentAcceptAll),
    takeEvery(consentActions.rejectNonEssential.type, handleConsentReject),
    takeEvery(consentActions.updateConsent.type, handleConsentUpdate),
  ])
}
